import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

const FeatureCard = ({ title, description, image, index }) => {
  // const { isDarkMode } = useTheme();

  return (
    <div className="relative rounded-[20px] bg-[#101012] border border-[#3E3C33] h-[350px] md:h-[380px] lg:h-[461px] font-lexend overflow-hidden">
      {/* Main Content */}
      <div className="relative h-full flex flex-col">
        {/* Image Container - Adjusted height ratio for better tablet view */}
        <div className="h-[55%] md:h-[58%] lg:h-[60%] w-full">
          <img 
            src={image} 
            alt={title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Text Content - Adjusted padding and spacing for tablet */}
        <div className="h-[45%] md:h-[42%] lg:h-[40%] text-left font-lexend flex flex-col justify-start p-4 md:p-5 lg:p-8">
          <h3 className="text-[20px] md:text-[22px] lg:text-[24px] font-bold text-[#fff] mb-2">
            {title}
          </h3>
          <p className="text-[14px] md:text-[15px] lg:text-[16px] text-[#fff] line-clamp-3">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

const HowItWorksSection = () => {
  const { isDarkMode } = useTheme();

  const steps = {
    row1: [
      {
        title: "Explore Quests",
        description: "Browse a range of AI Quests that match your skills and interests. Choose the ones that inspire you to innovate.",
        image: "/assets/Group 1861.png",
        width: "w-[350px] md:w-[450px] lg:w-[457px]"
      },
      {
        title: "Collaborate & Create",
        description: "Work individually or team up with other global data scientists to develop solutions, enhancing your collaborative and technical skills.",
        image: "/assets/group 2.png",
        width: "w-[350px] md:w-[450px] lg:w-[753px]"
      }
    ],
    row2: [
      {
        title: "Submit & Get Reviewed",
        description: "Submit your solution for peer review or evaluation by the hosting organization's reviewers. All submissions are securely encrypted and stored on the blockchain.",
        image: "/assets/Group 3.png",
        width: "w-[350px] md:w-[450px] lg:w-[605px]"
      },
      {
        title: "Earn Rewards & Co-Own Solutions",
        description: "If your solution is selected, you'll be rewarded and share ownership of the final AI asset with the hosting organization.",
        image: "/assets/Group 4.png",
        width: "w-[350px] md:w-[450px] lg:w-[605px]"
      }
    ]
  };

  return (
    <section className={`relative py-8 md:py-10 lg:py-16 px-4 md:px-6 lg:px-4 font-lexend flex justify-center ${isDarkMode ? 'bg-[#000000]' : 'bg-white'}`}>
      <div className="max-w-[1300px] w-full mx-auto font-lexend">
        {/* Header */}
        <div className="mb-8 md:mb-8 lg:mb-12 max-w-[350px] md:max-w-[900px] lg:max-w-[1300px] mx-auto md:mx-0">
          <motion.h2 
            className={`text-[28px] md:text-[36px] text-center md:text-left font-bold ${isDarkMode ? 'text-[#fff]' : 'text-[#000]'} mb-4`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            How It Works for Participants<br className="hidden md:block" />to Excel and Win
          </motion.h2>
          <motion.p 
            className="text-[14px] md:text-[16px] lg:text-[18px] max-w-full md:max-w-[700px] lg:max-w-[720px] font-lexend text-center md:text-left text-gray-400"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Explore an exciting journey where participants compete, innovate, and excel by solving real-world challenges effortlessly.
          </motion.p>
        </div>

        {/* Cards Container */}
        <div className="flex flex-col gap-4 md:gap-8 lg:gap-8 w-full font-lexend">
          {/* First Row */}
          <div className="flex flex-col md:flex-row gap-4 md:gap-8 lg:gap-6 w-full justify-center md:justify-start">
            {steps.row1.map((step, index) => (
              <motion.div
                key={index}
                className={`${step.width} mx-auto md:mx-0`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <FeatureCard {...step} index={index} />
              </motion.div>
            ))}
          </div>

          {/* Second Row */}
          <div className="flex flex-col md:flex-row gap-4 md:gap-8 lg:gap-6 w-full justify-center md:justify-start">
            {steps.row2.map((step, index) => (
              <motion.div
                key={index}
                className={`${step.width} mx-auto md:mx-0`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <FeatureCard {...step} index={index + 2} />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection; 
